import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Alert,
  BlankState,
  Box,
  Button,
  ContentTemplate,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableMeta,
  Pill,
  SectionHeader,
  TitleHeader,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useMediaQuery } from '@mui/material';
import RouteKey from '../../routes/key';
import { AppealApplicationRecordsResponse } from '../../api/candidate/appeal/model';
import { candidateService } from '../../api';
import { stringToSemanticDisplay } from '../../utils/DateUtils';
import { parseLocationSearch } from '../../utils/StringUtils';

const { appeal } = RouteKey.child;
const { importantNotes, summary, applications } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  viewDetailsButton: {
    display: 'flex',
    alignSelf: 'flex-start',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  appealStartButton: {
    height: 'fit-content',
    marginBottom: theme.spacing(3),
    '@supports (-webkit-touch-callout: none)': {
      '@media (hover: none)': {
        '&:hover': {
          backgroundColor: `${theme.color.link.linkDefault02} !important`,
          boxShadow: 'none',
        },
      },
    },
  },
  pillContainer: {},
  dataTable: {
    marginBottom: theme.spacing(7),
  },
}));

enum StatusOptions {
  submitted = 'SUBMITTED',
  registered = 'REGISTERED',
  draft = 'DRAFT',
}

interface ViewAppealPageProps {
  title: string;
}

const getBlankStateDisplayButton = (
  data: AppealApplicationRecordsResponse,
  t: any,
  classes: any,
  handleBackToHome: () => void,
  handleStartAppeal: () => void
) => {
  if (data.appealOpen) {
    return (
      <Button
        disabled={!data.resultReleaseOpen}
        id="button-startNewAppealDatatable"
        size="small"
        color="primary"
        variant="text"
        onClick={handleStartAppeal}
      >
        {t('appeal->>AppealListing.SubmitAnAppeal')}
      </Button>
    );
  }
  return (
    <Button
      id="button-backToHomepageDatatable"
      size="small"
      color="primary"
      variant="text"
      onClick={handleBackToHome}
    >
      {t('appeal->>AppealListing.BackToHomepage')}
    </Button>
  );
};

const generateTableOptions = (
  data: AppealApplicationRecordsResponse,
  t: any,
  classes: any,
  handleBackToHome: () => void,
  handleStartAppeal: () => void
) => ({
  textLabels: {
    body: {
      noMatch: (
        <>
          <Box className={classes.emptyReserve}>
            <></>
          </Box>
          <Box className={classes.emptyContainer}>
            <BlankState
              label={
                data.appealOpen
                  ? t('appeal->>AppealListing.NoPendingAppeal')
                  : t('appeal->>AppealListing.AppealUnavailable')
              }
            />
            {getBlankStateDisplayButton(data, t, classes, handleBackToHome, handleStartAppeal)}
          </Box>
        </>
      ),
    },
  },
});

const AppealImportantNotes = ({ title }: ViewAppealPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const header = t('appeal->>View.Appeal for Review of Results Applications');
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation<{ uuid?: string }>();
  const queryParams = parseLocationSearch(location.search);

  const {
    data: appealApplicationRecordsData,
  } = candidateService.appeal.candidate.useGetAppealApplicationRecords(queryParams.uuid);

  const handleViewDetails = (uuid: string) => {
    history.push(`/${appeal}/${applications.route}/${summary.route}?uuid=${uuid}`);
  };

  const handleStartAppeal = () => {
    history.push(`/${appeal}/${applications.route}/${importantNotes.route}`);
  };
  const handleBackToHome = () => {
    history.push(`/`);
  };

  const applicationStatusCustomBody = (value: string) => {
    switch (true) {
      case value === StatusOptions.submitted:
      case value === StatusOptions.registered:
        return (
          <Pill
            className={classes.pillContainer}
            label={t('appeal->>View.Submitted')}
            variant="positive"
          />
        );
      default:
        return null;
    }
  };

  const uuidCustomBody = (value: any, tableMeta: MUIDataTableMeta) => (
    <>
      {tableMeta.rowData[3] === StatusOptions.submitted ||
        (tableMeta.rowData[3] === StatusOptions.registered && (
          <Button
            color="primary"
            size="small"
            variant="text"
            className={classes.viewDetailsButton}
            onClick={() => handleViewDetails(tableMeta.rowData[5])}
          >
            {t('appeal->>View.View Details')}
          </Button>
        ))}
    </>
  );

  const tableColumns: MUIDataTableColumnDef[] = [
    {
      name: 'applicationId',
      label: 'Application ID',
    },
    {
      name: 'examLevel',
      label: 'Exam Level(s)',
    },
    {
      name: 'dateOfApplication',
      label: 'Application Date',
      options: {
        customBodyRender: (value: string) => stringToSemanticDisplay(value),
      },
    },
    {
      name: 'applicationStatus',
      label: 'Status',
      options: {
        customBodyRender: applicationStatusCustomBody,
      },
    },
    {
      name: 'appealOutcomeDateTime',
      label: 'Appeal Outcome',
      options: {
        customBodyRender: (value: string) =>
          value ? `Last updated on ${stringToSemanticDisplay(value)}` : '-',
      },
    },
    {
      name: 'uuid',
      label: 'Action',
      options: {
        customBodyRender: uuidCustomBody,
      },
    },
  ];

  return (<>
    <TitleHeader title={title} />
    {appealApplicationRecordsData && (
      <ContentTemplate type="full" continuous transparent>
        <Grid className={classes.headerContainer} container justify="space-between">
          <SectionHeader title={header} />
          <Grid xs={12} md={4} container justify="flex-end">
            <Button
              id="button-View.Appeal-start"
              fullWidth={isMobile}
              onClick={handleStartAppeal}
              disabled={
                !appealApplicationRecordsData.resultReleaseOpen ||
                !appealApplicationRecordsData.appealOpen
              }
              className={classes.appealStartButton}
            >
              {t('appeal->>View.Submit an Appeal')}
            </Button>
          </Grid>
        </Grid>
        {!appealApplicationRecordsData.resultReleaseOpen && (
          <Alert
            className={classes.alert}
            variant="filled"
            severity="error"
            message={t('appeal->>AppealListing.ResultUnavailable')}
          />
        )}
        <Grid className={classes.dataTable}>
          <MUIDataTable
            columns={tableColumns}
            data={appealApplicationRecordsData.results}
            options={[generateTableOptions(
              appealApplicationRecordsData,
              t,
              classes,
              handleBackToHome,
              handleStartAppeal
            )]}
            components={[]}
          />
        </Grid>
      </ContentTemplate>
    )}
  </>);
};

export default AppealImportantNotes;
